import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useAtomValue, useSetAtom } from "jotai";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiSearch, BiSortAlt2 } from "react-icons/bi";
import { CiFilter } from "react-icons/ci";
import { useInView } from "react-intersection-observer";

import type { ComparitorFilterTypeEnum, FilterTypeEnum, SetCodeEnum } from "@sorcery/db";
import { ADVANCED_FILTER_TYPES } from "@sorcery/db";
import {
  Input,
  LoadingSpinner,
  NoResults,
  OnClickLink,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tab,
} from "@sorcery/ui";

import { Collapse } from "~/components/atoms/Collapse";
import { CardPreviewList } from "~/components/molecules/card/CardPreviewList";
import { FinishFilters } from "~/components/molecules/filters/FinishFilters";
import type { AdvancedSearchPayload } from "~/providers/ModalProvider";
import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { collectionJotai } from "~/state/collectionJotai";
import { deckJotai } from "~/state/deckJotai";
import { searchJotai, setSearchJotai } from "~/state/searchJotai";

import type { CardSortEnum } from "~/types/card";
import { SEARCH_SORT, cardSortOptions } from "~/utils/filters";
import { api } from "~/utils/trpc";
import { ElementFilters } from "../../molecules/filters/ElementFilters";
import { RarityFilters } from "../../molecules/filters/RarityFilters";
import { TypeFilters } from "../../molecules/filters/TypeFilters";
import { CollectionPreviewList } from "../collection/collectionpreview/CollectionPreviewList";

export const SearchCards = () => {
  /************************************************************************************
   * initialization
   */
  const { openModal } = useModal();
  const collection = useAtomValue(collectionJotai);
  const deck = useAtomValue(deckJotai);
  const [parent] = useAutoAnimate();

  const { ref: infScrollRef, inView: infScrollTriggerInView } = useInView({
    root: null,
    rootMargin: "0px",
    threshold: 0.9,
    fallbackInView: true,
  });

  // searching/filtering/sorting
  const search = useAtomValue(searchJotai);
  const setSearch = useSetAtom(setSearchJotai);

  const advancedFilters = useMemo(
    () =>
      search?.filters.filter((f) =>
        ADVANCED_FILTER_TYPES.includes(f.type as FilterTypeEnum | ComparitorFilterTypeEnum),
      ) ?? [],
    [search?.filters],
  );

  const [quickFiltersOpen, setQuickFiltersOpen] = useState(!!search?.filters.length);

  /************************************************************************************
   * data fetching
   */
  // sets
  const { data: sets } = api.card.getSets.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // cards
  const { data, isFetching, isRefetching, fetchNextPage, isFetchingNextPage } =
    api.card.search.useInfiniteQuery(
      {
        query: search?.query ?? "",
        sort: search?.csort ?? SEARCH_SORT,
        set: search?.set ?? "*",
        filters: search?.filters ?? [],
        limit: 30,
        // limit the variants returned when viewing a collection, but return all variants when not - allows the deck and card lib popups to hold all variants for browsing but still show the relevant one in preview
        variantLimit: !!collection,
      },
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    );

  // potential count
  const { data: count } = api.card.count.useQuery(
    {
      query: search?.query ?? "",
      set: search?.set ?? "*",
      filters: search?.filters ?? [],
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  /*************************************************************************************************************************
   * react helper hooks
   */
  const cards = useMemo(() => {
    return data?.pages.flatMap((page) => page.cards) ?? [];
  }, [data]);

  const setOptions = useMemo(() => {
    return [{ label: "Any Set", value: "*" }].concat(
      (sets ?? []).map((s) => ({
        label: s.name,
        value: s.code,
      })),
    );
  }, [sets]);

  const queryChangeHandler = useCallback(
    (query: string) => {
      setSearch({
        query,
      });
    },
    [setSearch],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(queryChangeHandler, 500),
    [queryChangeHandler],
  );

  const loading = useMemo(() => {
    return (isFetching && !isRefetching) || isFetchingNextPage;
  }, [isFetching, isFetchingNextPage, isRefetching]);

  const showNoResultsSplash = useMemo(
    () => !cards?.length && !isFetching,
    [cards?.length, isFetching],
  );

  const renderedResults = useMemo(() => {
    return (
      <div className="mt-4">
        {collection ? (
          <CollectionPreviewList cards={cards ?? []} />
        ) : (
          <CardPreviewList cards={cards ?? []} />
        )}
      </div>
    );
  }, [cards, collection]);

  const handleAdvancedSearch = ({ newFilters }: AdvancedSearchPayload) => {
    setSearch({
      filters: newFilters,
    });
  };

  /*************************************************************************************************************************
   * side effects
   */
  // Trigger inf scroll
  useEffect(() => {
    if (infScrollTriggerInView) {
      void fetchNextPage();
    }
  }, [fetchNextPage, infScrollTriggerInView]);

  /*************************************************************************************************************************
   * render
   */
  return (
    <>
      {/* puts everything in line on large screens, right aligning the search options, or stacks on mobile */}
      <div
        className={`flex w-full flex-col justify-between md:mb-2 ${
          deck ? "xl:flex-row xl:gap-12" : "lg:flex-row lg:gap-12" // is this needed?
        }`}
      >
        <div className="flex w-full flex-col md:grow md:flex-row">
          {/* Search */}
          <div className="mr-1 w-full max-w-md">
            <Input
              defaultValue={search?.query ?? ""}
              onChange={debouncedChangeHandler}
              placeholder="Search cards..."
              LeftComponent={BiSearch}
            />
          </div>

          {/* Sorting and filtering */}
          <div className="mb-2 flex">
            {/* Set */}
            <span className="mr-1">
              <Select onValueChange={(v) => setSearch({ set: v as SetCodeEnum | "*" })}>
                <SelectTrigger>
                  <CiFilter size={16} />
                  <SelectValue
                    placeholder={
                      setOptions.find(
                        (s) => (s.value as SetCodeEnum | "*") === (search?.set ?? "*"),
                      )?.label ?? "Any Set"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {setOptions.map((s) => (
                    <SelectItem key={s.label} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </span>
            {/* Sorting */}
            <span className="mr-1">
              <Select onValueChange={(v) => setSearch({ csort: v as CardSortEnum })}>
                <SelectTrigger>
                  <BiSortAlt2 size={16} />
                  <SelectValue
                    placeholder={
                      cardSortOptions.find((s) => s.value === search?.csort)?.label ?? "Name" // todo default to sorting by release time
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {cardSortOptions.map((s) => (
                    <SelectItem key={s.label} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </span>
          </div>
        </div>
      </div>

      {/* UI under search */}
      <div className="mb-2 flex items-center">
        {/* Filtering & advanced search */}
        <div className="flex items-center pr-2 sm:pr-0">
          <Tab
            title="Quick Filters"
            onSelect={() => setQuickFiltersOpen(!quickFiltersOpen)}
            active={!!quickFiltersOpen}
          />

          <button type="button" className="focus:outline-white focus:outline-1">
            <OnClickLink
              className="whitespace-nowrap pr-3 sm:pr-5 select-none"
              color={"primary"}
              onClick={() =>
                openModal(ModalTypeEnum.ADVANCEDCARDSEARCH, {
                  filters: search?.filters ?? [],
                  onConfirm: handleAdvancedSearch,
                })
              }
            >
              <span>Advanced&nbsp;</span>
              <span className="hidden sm:inline">Search </span>
            </OnClickLink>
          </button>
        </div>

        {!loading && (
          <div className="border-l border-l-line-color pl-5">
            <span className="pr-3 sm:pr-5 whitespace-nowrap">{`${count ?? 0} Results`}</span>

            {!!search?.filters.length && (
              <OnClickLink
                className="whitespace-nowrap"
                color={"warning"}
                onClick={() => {
                  setSearch({
                    filters: [],
                  });
                }}
              >
                Reset Filters
              </OnClickLink>
            )}
          </div>
        )}
      </div>

      {/* Quick filters */}
      <Collapse open={quickFiltersOpen}>
        <TypeFilters />
        <ElementFilters />
        <RarityFilters />
        <FinishFilters />
      </Collapse>

      {/* AS icons mobile */}
      {!!advancedFilters.length && (
        <div className={"mb-2"}>
          <div className="flex flex-row flex-wrap" ref={parent}>
            {advancedFilters.map((f, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <div className="mb-1" key={i}>
                <OnClickLink
                  onClick={() =>
                    setSearch({
                      filters: search?.filters.filter(
                        (filly) => filly.label !== f.label, // requires no duplicate label filters exist, which intuitively they shouldnt
                      ),
                    })
                  }
                >
                  <span className="mr-2 whitespace-nowrap rounded bg-purple-900 px-2.5 py-0.5 text-sm text-purple-300">
                    <span>{f.label}</span>{" "}
                    <span>{f.comparitor ? `${f.comparitor} ${f.value}` : ""}</span>
                    <span>&nbsp; x</span>
                  </span>
                </OnClickLink>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* results */}
      {showNoResultsSplash ? <NoResults /> : renderedResults}

      {/* loading */}
      <div className="h-8">
        {loading && (
          <div className="mt-6 flex justify-center duration-500 animate-in fade-in sm:justify-start">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* inf query trigger */}
      <span className="invisible" ref={infScrollRef} />
    </>
  );
};
